import { useMemo } from 'react';

interface JsonDiffProps {
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

export default function JsonDiff({ before, after }: JsonDiffProps) {
  const rows = useMemo(() => {
    const keys = new Set<string>([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
    return Array.from(keys)
      .sort()
      .map((key) => {
        const oldVal = formatValue(before?.[key]);
        const newVal = formatValue(after?.[key]);
        return { key, oldVal, newVal, changed: oldVal !== newVal };
      })
      .filter((r) => r.changed);
  }, [before, after]);

  if (rows.length === 0) {
    return <p className="text-sm text-gray-500 italic">No field-level differences detected.</p>;
  }

  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="text-left text-gray-500 border-b border-gray-200">
          <th className="py-1 pr-4 font-medium">Field</th>
          <th className="py-1 pr-4 font-medium">Before</th>
          <th className="py-1 font-medium">After</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-b border-gray-100 align-top">
            <td className="py-1 pr-4 font-mono text-xs text-gray-700">{r.key}</td>
            <td className="py-1 pr-4 font-mono text-xs text-red-600 line-through break-all">{r.oldVal}</td>
            <td className="py-1 font-mono text-xs text-green-700 break-all">{r.newVal}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
